import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';

import { color, image } from '@/theme';
import { PrimaryButton } from '@/components/button';
import {
  ArenaFileUpload,
  InputBasic,
  InputDate,
  RadioInput,
  SelectInput,
} from '@/components/input';
import { GoogleRecaptcha } from '@/components/item';
import { LoadingSpinner } from '@/components/loading';
import { Image } from '@/components/shared';
import { RegularText } from '@/components/text';
import { InquiryTerms } from '@/pages/inquiry';
import { ButtonContent, ButtonWrapper, InputRows } from '@/pages/inquiry/inquryStyles';
import { mq } from '@/utils/mediaQuery';

const Warning = styled.div(({ theme }) => ({
  margin: '1.2rem 0 3.2rem',
  fontFamily: theme.fontFamily.primaryFont,
  fontWeight: theme.fontWeight.bold,
  fontSize: 'clamp(0.9rem, 2.8vw, 1.4rem)',
  whiteSpace: 'pre-line ',

  [mq('tablet')]: {
    fontSize: '1.8rem',
  },
}));

const ArenaRequestDetails = ({
  control,
  setStep,
  setValue,
  errors,
  fileList,
  setFileList,
  stageType,
  audienceType,
  isPending,
}) => {
  const intl = useIntl();

  const goToPrev = useCallback(() => {
    setStep(1);
  }, [setStep]);

  const handleFileListChange = useCallback(
    (updater) => {
      setFileList((prev) => {
        const newState = updater(prev);

        setValue('fileList_business', newState.business, { shouldValidate: true });
        setValue('fileList_other', newState.other, { shouldValidate: true });

        return newState;
      });
    },
    [setFileList, setValue]
  );

  return (
    <>
      <InputBasic
        control={control}
        name="event_name"
        label="lang-event-name"
        placeholder="lang-placeholder-event"
        maxLength={50}
        required
      />

      <InputBasic
        type="number"
        control={control}
        name="attendees"
        label="lang-attendees-arena"
        placeholder="lang-placeholder-attendees-arena"
        maxLength={50}
        required
      />

      <InputRows>
        <InputDate
          control={control}
          name="event_rental_start_date"
          label="lang-event-start-date"
          errors={errors}
          required
        />

        <InputDate
          control={control}
          name="event_rental_end_date"
          label="lang-event-end-date"
          errors={errors}
          required
        />
      </InputRows>

      <InputRows>
        <InputDate
          control={control}
          name="preparation_rental_start_date"
          label="lang-preparation-start-date"
          errors={errors}
        />

        <InputDate
          control={control}
          name="preparation_rental_end_date"
          label="lang-preparation-end-date"
          errors={errors}
        />
      </InputRows>

      <InputRows>
        <InputDate
          control={control}
          name="withdrawal_rental_start_date"
          label="lang-dismantle-start-date"
          errors={errors}
        />

        <InputDate
          control={control}
          name="withdrawal_rental_end_date"
          label="lang-dismantle-end-date"
          errors={errors}
        />
      </InputRows>

      <div style={{ marginBottom: '2rem' }}>
        <InputBasic
          control={control}
          name="schedule"
          label="lang-backup-schedule"
          multiline
          rows={4}
          placeholder="lang-placeholder-backup-schedule"
          maxLength={500}
        />
      </div>

      <SelectInput
        control={control}
        errors={errors}
        name="stage_type"
        arr={stageType}
        label="lang-stage-type"
        required
      />

      <RadioInput
        control={control}
        name="audience_type"
        label="lang-audience-type"
        arr={audienceType}
        required
      />

      <InputBasic
        control={control}
        name="comments"
        label="lang-requirements"
        multiline
        rows={10}
        placeholder="lang-placeholder-requirements"
        maxLength={500}
      />

      <ArenaFileUpload
        fileList={fileList}
        setFileList={handleFileListChange}
        description1={intl.formatMessage({ id: 'lang-upload-file-description-1' })}
        description2={intl.formatMessage({ id: 'lang-upload-file-description-2' })}
        errors={errors}
      />

      <Warning>
        <FormattedMessage id="lang-complete-form-warning" />
      </Warning>

      <InquiryTerms control={control} errors={errors} setValue={setValue} />

      <GoogleRecaptcha setValue={setValue} errors={errors} />

      <ButtonWrapper>
        {isPending ? (
          <LoadingSpinner />
        ) : (
          <>
            <PrimaryButton type="button" onClick={goToPrev}>
              <ButtonContent>
                <Image src={image.arrowLeft.default} width="1.8rem" height="1.4rem" />

                <RegularText id="lang-prev" color={color.base_white} />
              </ButtonContent>
            </PrimaryButton>

            <PrimaryButton type="submit" width="92%">
              <ButtonContent>
                <RegularText id="lang-inquiry-submit" color={color.base_white} />

                <Image src={image.arrowRight.default} width="1.8rem" height="1.4rem" />
              </ButtonContent>
            </PrimaryButton>
          </>
        )}
      </ButtonWrapper>
    </>
  );
};

export default ArenaRequestDetails;
